import {
    FaceLandmarker,
    ObjectDetector,
    FilesetResolver,
} from "@mediapipe/tasks-vision";

class Mediapipe {
    constructor() {
        this.handlers = {};
        this.callbacks = {
            on_violation_detected: null,
            show_notification: null,
        };

        this.faceLandmarker = null;
        this.objectDetector = null;
        this.isInitialized = false;

        this.video = null;
        this.stream = null;
        this.running = false;
        this.isProcessing = false;
        this.lastDetectionTime = 0;

        this.config = {
            max_retries: 3,
            initial_retry_delay_ms: 1000,
            detection_interval_ms: 1000,
            violation_persistence_ms: 3000,
            face_alert_cooldown_ms: 5000,
            head_rotation_alert_cooldown_ms: 5000,
            cellphone_alert_cooldown_ms: 5000,
            head_rotation_threshold: { pitch: 30, yaw: 35, roll: 25 },
            cellphone_score_threshold: 0.55,
        };

        this.violation_start_timers = {
            no_face: null,
            multiple_faces: null,
            head_turn: null,
            cellphone_detected: null,
        };

        this.last_alert_timestamps = {
            no_face: 0,
            multiple_faces: 0,
            head_turn: 0,
            cellphone_detected: 0,
        };
    }

    _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    getHeadRotation(matrixes) {
        const data = matrixes?.[0]?.data;
        if (!data) return null;

        const z = { x: data[8], y: data[9], z: data[10] };
        const y = { x: data[4], y: data[5], z: data[6] };

        const pitch = Math.asin(-z.y) * (180 / Math.PI);
        const yaw = Math.atan2(z.x, z.z) * (180 / Math.PI);
        const roll = Math.atan2(y.x, y.y) * (180 / Math.PI);

        return { pitch, yaw, roll };
    }

    _registerDefaultHandlers() {
        const violations = [
            {
                id: "no_face",
                title: "No Face Detected",
                msg: "No face detected. Please stay visible.",
            },
            {
                id: "multiple_faces",
                title: "Multiple Faces Detected",
                msg: "Only one person should be visible.",
            },
            {
                id: "head_turn",
                title: "Head Turn Detected",
                msg: "Please face the screen directly.",
            },
            {
                id: "cellphone_detected",
                title: "Cellphone Detected",
                msg: "Please remove the cellphone from view.",
            },
        ];

        violations.forEach(({ id, title, msg }) => {
            this.handlers[id] = (customMsg) => {
                this.callbacks.show_notification?.(
                    title,
                    customMsg || msg,
                    "warning",
                );
                this.callbacks.on_violation_detected?.(id);
            };
        });
    }

    _checkViolationPersistence(key, isActive, cooldownMs) {
        const now = Date.now();

        if (isActive) {
            if (!this.violation_start_timers[key]) {
                this.violation_start_timers[key] = now;
            }

            const duration = now - this.violation_start_timers[key];
            const cooldown = now - this.last_alert_timestamps[key];

            if (
                duration >= this.config.violation_persistence_ms &&
                cooldown >= cooldownMs
            ) {
                this.last_alert_timestamps[key] = now;
                return true;
            }
        } else {
            this.violation_start_timers[key] = null;
        }
        return false;
    }

    async createModelsWithDelegate(fileset, delegate) {
        const commonBase = { delegate };

        const [faceLandmarker, objectDetector] = await Promise.all([
            FaceLandmarker.createFromOptions(fileset, {
                baseOptions: {
                    ...commonBase,
                    modelAssetPath:
                        "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
                },
                runningMode: "VIDEO",
                numFaces: 2,
                outputFacialTransformationMatrixes: true,
            }),
            ObjectDetector.createFromOptions(fileset, {
                baseOptions: {
                    ...commonBase,
                    modelAssetPath:
                        "https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite",
                },
                runningMode: "VIDEO",
                scoreThreshold: this.config.cellphone_score_threshold,
            }),
        ]);

        return { faceLandmarker, objectDetector };
    }

    async initializeModels() {
        const wasmUrl =
            "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.12/wasm";

        for (let attempt = 1; attempt <= this.config.max_retries; attempt++) {
            try {
                const fileset = await FilesetResolver.forVisionTasks(wasmUrl);
                let models;

                try {
                    models = await this.createModelsWithDelegate(
                        fileset,
                        "GPU",
                    );
                } catch {
                    models = await this.createModelsWithDelegate(
                        fileset,
                        "CPU",
                    );
                }

                this.faceLandmarker = models.faceLandmarker;
                this.objectDetector = models.objectDetector;
                this.isInitialized = true;
                return;
            } catch (e) {
                if (attempt === this.config.max_retries) throw e;
                await this._sleep(
                    this.config.initial_retry_delay_ms *
                        Math.pow(2, attempt - 1),
                );
            }
        }
    }

    handleFaceResults(results) {
        const alerts = [];
        const faceCount = results.faceLandmarks.length;

        if (
            this._checkViolationPersistence(
                "no_face",
                faceCount === 0,
                this.config.face_alert_cooldown_ms,
            )
        ) {
            alerts.push({ type: "no_face" });
        }

        if (
            this._checkViolationPersistence(
                "multiple_faces",
                faceCount > 1,
                this.config.face_alert_cooldown_ms,
            )
        ) {
            alerts.push({ type: "multiple_faces" });
        }

        if (faceCount === 1) {
            const rotation = this.getHeadRotation(
                results.facialTransformationMatrixes,
            );
            if (rotation) {
                const { pitch, yaw, roll } = rotation;
                const t = this.config.head_rotation_threshold;

                const turned =
                    Math.abs(pitch) > t.pitch ||
                    Math.abs(yaw) > t.yaw ||
                    Math.abs(roll) > t.roll;

                if (
                    this._checkViolationPersistence(
                        "head_turn",
                        turned,
                        this.config.head_rotation_alert_cooldown_ms,
                    )
                ) {
                    alerts.push({ type: "head_turn" });
                }
            }
        } else {
            this.violation_start_timers.head_turn = null;
        }

        return alerts;
    }

    handleObjectResults(results) {
        const isCellphone = results.detections?.some((d) =>
            d.categories.some(
                (c) =>
                    c.categoryName === "cell phone" &&
                    c.score >= this.config.cellphone_score_threshold,
            ),
        );

        if (
            this._checkViolationPersistence(
                "cellphone_detected",
                isCellphone,
                this.config.cellphone_alert_cooldown_ms,
            )
        ) {
            return [{ type: "cellphone_detected" }];
        }

        return [];
    }

    processFrame(timestamp) {
        if (!this.isInitialized || !this.video) return [];

        const alerts = [];

        try {
            const faceResults = this.faceLandmarker.detectForVideo(
                this.video,
                timestamp,
            );
            const objectResults = this.objectDetector.detectForVideo(
                this.video,
                timestamp,
            );

            alerts.push(...this.handleFaceResults(faceResults));
            alerts.push(...this.handleObjectResults(objectResults));
        } catch {
            /* silent */
        }

        return alerts;
    }

    detectionLoop() {
        if (!this.running) return;

        requestAnimationFrame(() => this.detectionLoop());

        const now = performance.now();
        if (
            now - this.lastDetectionTime < this.config.detection_interval_ms ||
            this.isProcessing
        )
            return;

        this.lastDetectionTime = now;

        if (this.video.readyState >= 2) {
            this.isProcessing = true;
            const alerts = this.processFrame(now);
            alerts.forEach((a) => this.dispatch(a.type));
            this.isProcessing = false;
        }
    }

    async start(videoTrack, videoElement) {
        this.video = videoElement;
        this.stream = new MediaStream([videoTrack]);
        this.video.srcObject = this.stream;

        return new Promise((resolve, reject) => {
            this.video.onloadeddata = async () => {
                try {
                    await this.video.play();
                    await this.initializeModels();
                    this.running = true;
                    this.detectionLoop();
                    resolve();
                } catch (e) {
                    reject(e);
                }
            };
            this.video.onerror = () => reject(new Error("Video load failed"));
        });
    }

    dispatch(type) {
        this.handlers[type]?.();
    }

    stop() {
        this.running = false;
        this.stream?.getTracks().forEach((t) => t.stop());
        this.faceLandmarker?.close();
        this.objectDetector?.close();
        this.faceLandmarker = this.objectDetector = this.video = null;
        this.isInitialized = false;
        this.handlers = {};
    }

    init(options = {}) {
        this.callbacks.on_violation_detected =
            options.on_violation_detected ||
            options.onViolationDetected ||
            null;
        this.callbacks.show_notification =
            options.show_notification || options.showNotification || null;

        this.config = { ...this.config, ...options.config };
        this._registerDefaultHandlers();
    }
}

export default new Mediapipe();
